import { getProficiencyLevelIconColor } from '../../../utils/competencies';

import type { CompetencyNote } from '@/pages/ManagerAssessment/Results/components/NotesResourcesTab';
import type { CompanyValueCardProps } from '@/pages/shared/ManagerAssessment/CompanyValueCard';
import type { CompanyValue } from '@/types/company-values';
import type { ProficiencyLevel } from '@/types/competencies/ProficiencyLevel';
import type { CycleInstance } from '@/types/cycle-evaluation/CycleInstance';
import type { EmployeeNote } from '@/types/employee-note';
import type { SkillItem } from '@/types/performance-profile';
import type { CompetencyGroupResult, CompetencyResult } from '@/types/review/CompetencyGroupResult';
import type { HandoutCompanyValueData } from '@/types/review/ReviewDiscussion';
import type { ReviewQuestion } from '@/types/review/ReviewQuestion';

import { noteHtmlToPlainText } from '@/pages/ManagerAssessment/utils/assessmentNoteText';
import { resolveCompanyValueIconBackground } from '@/utils/companyValueIconBackground';
import { getLocalizedText } from '@/utils/localization';
import { orderProficiencyLevels } from '@/utils/proficiencyLevel';

/**
 * Where a level sits on the scale, as a percentage. Levels are ordered best first (`sortOrder` 0),
 * so the first level reads 100 and the last reads 0.
 */
const levelPercentage = (levelIndex: number, levelCount: number): number => {
    const maxLevelIndex = Math.max(levelCount - 1, 1);

    return ((maxLevelIndex - levelIndex) / maxLevelIndex) * 100;
};

/**
 * The average level of the assessed competencies, as a percentage. Only a fallback for results
 * scored before the backend stored a global score; an unassessed competency is left out.
 */
export const averageCompetencyLevelPercentage = (
    groups: CompetencyGroupResult[],
    proficiencyLevels: ProficiencyLevel[],
): number => {
    const orderedLevels = orderProficiencyLevels(proficiencyLevels);
    const percentages = groups
        .flatMap((group) => group.competencies)
        .flatMap(({ proficiencyLevel }) => {
            const levelIndex = proficiencyLevel?.id
                ? orderedLevels.findIndex((level) => level.id === proficiencyLevel.id)
                : -1;

            return levelIndex === -1 ? [] : [levelPercentage(levelIndex, orderedLevels.length)];
        });

    if (percentages.length === 0) {
        return 0;
    }

    return percentages.reduce((sum, percentage) => sum + percentage, 0) / percentages.length;
};

export const enrichProficiencyLevelsWithColor = (levels: ProficiencyLevel[]): ProficiencyLevel[] =>
    levels.map((level) => ({
        ...level,
        color: getProficiencyLevelIconColor(level.icon),
    }));

export const buildCompanyValueCards = (
    cycleInstance: CycleInstance | undefined,
    companyValues: CompanyValue[],
    proficiencyLevels: ProficiencyLevel[],
    metricLabel: string,
): CompanyValueCardProps[] => {
    if (!cycleInstance?.includeCompanyValues) {
        return [];
    }

    const orderedLevels = orderProficiencyLevels(proficiencyLevels);
    const companyValueById = new Map(companyValues.map((value) => [value.id, value]));
    const levelById = new Map(orderedLevels.map((level) => [level.id ?? '', level] as const));

    const activeCompanyValues = companyValues.filter((value) => value.status === 'Active');
    const savedAssessments = cycleInstance.assessmentCompanyValues ?? [];

    const displayedValues: {
        companyValue: CompanyValue;
        assessedProficiencyLevelId?: string | null;
        score?: number | null;
    }[] =
        savedAssessments.length > 0
            ? savedAssessments
                  .filter((assessment) => companyValueById.has(assessment.companyValueId))
                  .map((assessment) => ({
                      companyValue: companyValueById.get(assessment.companyValueId)!,
                      assessedProficiencyLevelId: assessment.assessedProficiencyLevelId,
                      score: assessment.score,
                  }))
            : activeCompanyValues.map((companyValue) => ({
                  companyValue,
                  assessedProficiencyLevelId: undefined,
                  score: undefined,
              }));

    return displayedValues
        .map(
            ({
                companyValue,
                assessedProficiencyLevelId,
                score,
            }): { sortOrder: number; card: CompanyValueCardProps } => {
                const proficiencyLevel = assessedProficiencyLevelId
                    ? levelById.get(assessedProficiencyLevelId)
                    : undefined;
                const levelIndex = proficiencyLevel
                    ? orderedLevels.findIndex((level) => level.id === proficiencyLevel.id)
                    : -1;
                // Prefer the exact backend score; fall back to the banded level-derived percentage
                // only for legacy assessments saved before the score column existed.
                const metricValue =
                    score !== null && score !== undefined
                        ? Math.round(score)
                        : levelIndex >= 0
                          ? Math.round(levelPercentage(levelIndex, orderedLevels.length))
                          : 0;

                return {
                    sortOrder: companyValue.sortOrder,
                    card: {
                        title: getLocalizedText(companyValue.name),
                        description: getLocalizedText(companyValue.description),
                        icon: companyValue.icon,
                        cardBackgroundColor: resolveCompanyValueIconBackground(
                            companyValue.icon,
                            companyValue.iconBackground,
                        ),
                        badgeLabel: proficiencyLevel
                            ? getLocalizedText(proficiencyLevel.label)
                            : undefined,
                        metricLabel,
                        metricValue,
                        isEditable: false,
                    },
                };
            },
        )
        .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
        .map((item) => item.card);
};

export const mapCompetencyToSkillItem = (
    competency: CompetencyResult,
    cycleInstanceId: string,
    proficiencyLevels: ProficiencyLevel[],
): SkillItem => {
    const competencyProficiencyLevel = competency.lastAssessedProficiencyLevelId
        ? proficiencyLevels.find(
              (level) =>
                  level.id === competency.lastAssessedProficiencyLevelId && level.id !== undefined,
          )
        : null;

    const proficiencyLevel = competencyProficiencyLevel
        ? {
              id: competencyProficiencyLevel.id!,
              label: competencyProficiencyLevel.label,
              icon: competencyProficiencyLevel.icon,
              iconUrl: competencyProficiencyLevel.iconUrl ?? undefined,
              iconColor: competencyProficiencyLevel.iconColor ?? undefined,
              sortOrder: competencyProficiencyLevel.sortOrder || 0,
          }
        : null;

    return {
        id: competency.id,
        name: competency.name.en || competency.name.fr || competency.id,
        proficiency: proficiencyLevel,
        cycleInstanceId,
        changeHistory: [],
    };
};

export const mapCompetenciesToSkillItems = (
    competencies: CompetencyResult[],
    cycleInstanceId: string,
    proficiencyLevels: ProficiencyLevel[],
): SkillItem[] => {
    return competencies.map((competency) =>
        mapCompetencyToSkillItem(competency, cycleInstanceId, proficiencyLevels),
    );
};

export const mapHandoutCompanyValueToCardProps = (
    data: HandoutCompanyValueData,
    proficiencyLevels: ProficiencyLevel[],
): CompanyValueCardProps => {
    const orderedLevels = orderProficiencyLevels(proficiencyLevels);
    const proficiencyLevel = data.proficiencyLevelId
        ? orderedLevels.find((l) => l.id === data.proficiencyLevelId)
        : undefined;
    return {
        title: getLocalizedText(data.name),
        description: getLocalizedText(data.description),
        icon: data.icon,
        metricValue: data.assessedScore,
        metricLabel: 'Adherence',
        cardBackgroundColor: resolveCompanyValueIconBackground(data.icon, data.iconBackground),
        isEditable: false,
        dataTestId: `company-value-card-${data.id}`,
        badgeLabel: proficiencyLevel ? getLocalizedText(proficiencyLevel.label) : undefined,
        levelIcon: proficiencyLevel?.icon,
    };
};

interface QuestionNoteSource {
    id: string;
    questionId: string;
    content: string;
    updatedAt?: string;
}

const buildQuestionNotes = (
    cycleInstance: CycleInstance | undefined,
    assessmentNotes: EmployeeNote[],
): CompetencyNote[] => {
    const questions = cycleInstance?.reviewQuestionnaire?.reviewQuestions ?? [];
    const answers = cycleInstance?.managerAssessmentAnswers ?? [];
    // An answer carries its question too, which keeps the title of a comment whose question has
    // since left the questionnaire.
    const questionById = new Map<string | undefined, ReviewQuestion>([
        ...answers.map((answer) => [answer.reviewQuestionId, answer.reviewQuestion] as const),
        ...questions.map((question) => [question.id, question] as const),
    ]);
    const positionOf = (questionId: string) => {
        const position = questions.findIndex((question) => question.id === questionId);
        return position === -1 ? questions.length : position;
    };

    const linkedNotes: QuestionNoteSource[] = assessmentNotes.flatMap((note) =>
        note.reviewQuestionId
            ? [
                  {
                      id: note.id,
                      questionId: note.reviewQuestionId,
                      content: noteHtmlToPlainText(note.content),
                      updatedAt: note.updatedAt,
                  },
              ]
            : [],
    );
    const notedQuestionIds = new Set(linkedNotes.map((note) => note.questionId));
    const legacyComments: QuestionNoteSource[] = answers
        .filter((answer) => !notedQuestionIds.has(answer.reviewQuestionId))
        .map((answer) => ({
            id: answer.id ?? answer.reviewQuestionId,
            questionId: answer.reviewQuestionId,
            content: answer.comment ?? '',
            updatedAt: answer.updatedAt ?? undefined,
        }));

    return [...linkedNotes, ...legacyComments]
        .filter((source) => source.content.trim().length > 0)
        .sort((a, b) => positionOf(a.questionId) - positionOf(b.questionId))
        .map(({ id, questionId, content, updatedAt }) => {
            const question = questionById.get(questionId);
            return {
                id,
                title: getLocalizedText(question?.label) || '',
                content,
                updatedAt,
                competencyName: question?.competencyName,
                companyValueName: question?.companyValueName,
            };
        });
};

/**
 * The notes of the "Notes & resources" tab: the question notes, in questionnaire order, then the
 * notes of the meeting topics.
 *
 * A question's note is the employee note written under it during this assessment. Assessments
 * answered before those notes existed kept theirs in the answer's `comment`: that comment is shown
 * only while no note is linked to its question, so a note written since takes its place.
 *
 * `assessmentNotes` is `undefined` while they are still being read: question notes are left out
 * until then, rather than showing old comments that a linked note may be about to replace.
 */
export const buildManagerAssessmentNotes = (
    cycleInstance: CycleInstance | undefined,
    assessmentNotes: EmployeeNote[] | undefined,
): CompetencyNote[] => {
    const topicNotes = (cycleInstance?.meetingTopics ?? [])
        .slice()
        .sort((a, b) => a.sortOrder - b.sortOrder)
        .filter((topic) => topic.notes && topic.notes.trim().length > 0)
        .map((topic) => ({
            id: topic.id,
            title: topic.subject || '',
            content: topic.notes ?? '',
        }));

    if (!assessmentNotes) {
        return topicNotes;
    }

    return [...buildQuestionNotes(cycleInstance, assessmentNotes), ...topicNotes];
};
